import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
  withRouterConfig
} from '@angular/router'
import type { ApplicationConfig } from '@angular/core'
import {
  ErrorHandler,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection
} from '@angular/core'
import { provideServiceWorker } from '@angular/service-worker'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { ErrorInterceptor } from './_shared/services/interceptors/ErrorInterceptor'
import { RequestInterceptor } from './_shared/services/interceptors/RequestInterceptor'
import { AppErrorHandler } from './app-error-handler'
import { fetchTranslation } from './_shared/functions/translation'
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay'
import { PRODUCT_CONFIGURATION, type ProductConfiguration } from '@awork/_shared/services/product/product-configuration'
import { createAppRoutes } from './app.routes'

function onAppInit(): () => Promise<void> {
  return fetchTranslation()
}

/**
 * Binds one host product to the shared application and its routes.
 * @param {ProductConfiguration} productConfiguration Selected product.
 * @returns {ApplicationConfig} Application providers.
 */
export default (productConfiguration: ProductConfiguration): ApplicationConfig => ({
  providers: [
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false }
    },
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      createAppRoutes(productConfiguration),
      withPreloading(PreloadAllModules),
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' })
    ),
    provideServiceWorker('awork-service-worker.js', {
      enabled: !isDevMode()
    }),
    provideHttpClient(withInterceptorsFromDi(), withXhr()),
    provideAnimationsAsync(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: AppErrorHandler
    },
    provideAppInitializer(() => {
      const initializerFn = onAppInit()
      return initializerFn()
    }),
    { provide: PRODUCT_CONFIGURATION, useValue: productConfiguration }
  ]
})
